import * as React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import CssBaseline from '@mui/material/CssBaseline';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import HomeSharpIcon from '@mui/icons-material/HomeSharp';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { Groups, Settings } from '@mui/icons-material';
import { Avatar, Stack, Toolbar } from '@mui/material';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import LogoutIcon from '@mui/icons-material/Logout';
import IconButton from '@mui/material/IconButton';
import ParishesIcon from '@mui/icons-material/AccountBalance';

import { logoutUser } from 'redux/slices/user/userSlice';
import { setAnnouncementsTab, setBasketsTab } from 'redux/slices/common/appSlice';
import AppBarComponent from 'components/Common/AppBar';
import { AppDispatch, RootState } from 'redux/store';
import { ANNOUNCEMENTS } from 'types/announcements';
import { GIVING_BASKETS } from 'utils/constants';
import { notify } from './Toast';
import MuiLoader from './Loader';
import WalletIcon from 'assets/wallet.png';
import StaffIcon from 'assets/staff.png';
import routes from 'constants/routes';
import Colors from 'constants/colors';

interface AppBarComponentProps {
    children: React.ReactElement;
}
interface MenuItem {
    title: string;
    icon: React.ReactElement | string;
    navigation: string;
    setCurrentTab?: (dispatch: AppDispatch) => void;
    isVisible: boolean;
}

const drawerWidthInVh = 38; // Adjust the value as needed

const drawerWidth = `${drawerWidthInVh}vh`;

const { account, community, home, staff, wallet, announcements, parishes } = routes;

const excludedPaths = [account.main, community.main, home.main, staff.main, wallet.main, announcements.main, parishes.main];

export default function DrawerLeft({ children }: AppBarComponentProps) {
    const theme = useTheme();
    let navigation = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    let location = useLocation();
    const [open, setOpen] = React.useState(false);
    const { user, isMerchant } = useSelector((state: RootState) => ({
        user: state.user,
        isMerchant: state.auth?.isMerchant,
    }));

    const { userInfo, logoutLoading } = user || {};
    const { name, username, imgUrl } = userInfo || {};

    const menuItems = React.useMemo((): MenuItem[] => {
        return [
            {
                title: 'Home',
                icon: <HomeSharpIcon />,
                navigation: home.main,
                setCurrentTab: setBasketsTab(GIVING_BASKETS),
                isVisible: true,
            },
            {
                title: 'Parishes & Organizations',
                icon: <ParishesIcon />,
                navigation: parishes.main,
                isVisible: true,
            },
            {
                title: 'Announcements',
                icon: <NotificationsIcon />,
                navigation: announcements.main,
                setCurrentTab: setAnnouncementsTab(ANNOUNCEMENTS),
                isVisible: true,
            },
            {
                title: 'Community',
                icon: <Groups />,
                navigation: community.main,
                isVisible: true,
            },
            {
                title: 'Wallet',
                icon: WalletIcon,
                navigation: wallet.main,
                isVisible: true,
            },
            {
                title: 'Staff',
                icon: StaffIcon,
                navigation: staff.main,
                isVisible: !!isMerchant,
            },
            {
                title: 'My Account',
                icon: <Settings />,
                navigation: account.main,
                isVisible: true,
            },
        ];
    }, [isMerchant]);

    React.useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    const handleDrawerClose = () => {
        setOpen(false);
    };

    const handleDrawerToggle = () => {
        setOpen(!open);
    };

    const handelLogOutUser = async () => {
        try {
            await dispatch(logoutUser());
        } catch (err) {
            notify('Something went wrong!', 'error');
        }
    };
    const handleNavigation = () => {
        navigation(-1);
    };

    const handleMenuItemClick = (item: MenuItem) => {
        const { setCurrentTab } = item || {};
        setCurrentTab && dispatch(setCurrentTab);
        return item.navigation;
    };

    const drawer = (
        <Box sx={{ height: '100%', justifyContent: 'space-between', display: 'flex', flexDirection: 'column' }}>
            <Box>
                <Toolbar />
                <Box display="flex" flexDirection="column" alignItems="center" width="100%">
                    <Avatar sx={{ width: 140, height: 140 }} src={imgUrl} alt="User Avatar" />
                    <Typography
                        variant={'h6'}
                        style={{
                            marginTop: theme.spacing(1),
                            fontSize: 20,
                            textAlign: 'center',
                        }}
                    >
                        {name}
                    </Typography>

                    <Typography
                        variant={'h6'}
                        style={{
                            marginTop: theme.spacing(1),
                            color: '#6B7280',
                            fontSize: 16,
                        }}
                    >
                        {username}
                    </Typography>
                </Box>
                <Divider sx={{ marginBottom: 2 }} />

                <Stack>
                    {menuItems.map((item, index) => {
                        if (!item.isVisible) return null;
                        return (
                            <Link key={index} to={item.navigation} style={{ textDecoration: 'none' }}>
                                <ListItem
                                    key={item.title}
                                    disablePadding
                                    className={location.pathname === item.navigation ? 'selected' : ''}
                                    onClick={() => handleMenuItemClick(item)}
                                >
                                    <ListItemButton>
                                        {typeof item.icon === 'string' ? (
                                            <Avatar sx={{ width: 24, height: 24 }} src={item.icon} alt={item.title} />
                                        ) : (
                                            item.icon
                                        )}
                                        <ListItemText style={{ marginLeft: 16, color: Colors.darkTextColor }} primary={item.title} />
                                    </ListItemButton>
                                </ListItem>
                            </Link>
                        );
                    })}
                </Stack>
            </Box>
            <Box>
                <Box>
                    <Typography
                        variant={'h3'}
                        style={{
                            fontSize: 16,
                            textAlign: 'center',
                        }}
                    >
                        Catholic Community version 0.3.4
                    </Typography>
                </Box>
                <Box style={{ marginTop: 'auto' }}>
                    <Divider />
                    <Stack
                        onClick={handelLogOutUser}
                        direction="row"
                        alignItems="center"
                        sx={{ '&:hover': { backgroundColor: 'rgb(232,232,232, 1)' }, cursor: 'pointer' }}
                    >
                        <IconButton>
                            <LogoutIcon />
                        </IconButton>
                        <Typography sx={{ marginLeft: 1 }} variant="body2">
                            Logout
                        </Typography>
                    </Stack>
                </Box>
            </Box>
        </Box>
    );

    return (
        <>
            <MuiLoader absolute loading={logoutLoading} />
            <AppBarComponent handleDrawerClose={handleDrawerClose} open={open} handleDrawerOpen={handleDrawerToggle} />

            <Box sx={{ display: 'flex' }}>
                <CssBaseline />

                <Box component="nav" sx={{ flexShrink: { sm: 0 } }} aria-label="mailbox folders">
                    <Drawer
                        variant="temporary"
                        open={open}
                        onClose={handleDrawerClose}
                        ModalProps={{
                            keepMounted: true,
                        }}
                        sx={{
                            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
                        }}
                    >
                        <Toolbar />
                        {drawer}
                    </Drawer>
                </Box>

                <Box
                    component="main"
                    sx={{
                        flexGrow: 1,
                        p: { xs: 0, md: 3 },
                        width: { sm: `calc(100% - ${drawerWidth})` },
                        marginLeft: { lg: open ? `${drawerWidth}` : 0 },
                        backgroundColor: '#F2F2F6',
                        minHeight: '100vh',
                    }}
                >
                    <Toolbar />
                    {!open && !excludedPaths.includes(location.pathname) && (
                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                marginLeft: 2,
                            }}
                        >
                            {/* <Box> */}
                            <Stack direction="row" alignItems="center">
                                <IconButton onClick={handleNavigation}>
                                    <KeyboardBackspaceIcon />
                                </IconButton>
                                <Typography>Back</Typography>
                            </Stack>
                        </Box>
                    )}
                    {children}
                </Box>
            </Box>
        </>
    );
}
